import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, MessageCircle, Bell, BellOff, CreditCard, Briefcase, Siren, WifiOff, RefreshCw, MailOpen, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), now = new Date();
  return d.toDateString() === now.toDateString() ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
};
const CAT_ICON = { payment: CreditCard, booking: Briefcase, lead: Briefcase, order: Briefcase, emergency: Siren };

function useOnline() {
  const [on, setOn] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const up = () => setOn(true), down = () => setOn(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return on;
}

function ThreadRow({ t, onMarkUnread }) {
  const { t: tr } = useI18n();
  return (
    <div className="flex items-center gap-3 p-3 hover:bg-stone-50 transition-colors" data-testid={`messenger-thread-${t.conv_id}`}>
      <Link to={`/messenger/${t.conv_id}`} className="flex items-center gap-3 flex-1 min-w-0" data-testid={`inbox-open-thread-${t.conv_id}`}>
        {t.other?.picture ? <img loading="lazy" src={t.other.picture} alt="" className="w-11 h-11 rounded-full object-cover shrink-0" />
          : <div className="w-11 h-11 shrink-0 rounded-full bg-gradient-to-br from-[#E05E36] to-[#2D5A27] grid place-items-center text-white font-bold">{t.other?.name?.[0] || "U"}</div>}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className={`text-sm text-stone-900 truncate ${t.unread ? "font-black" : "font-bold"}`}>{t.other?.name || "User"}</div>
            <div className="text-[10px] text-stone-500 shrink-0">{relTime(t.last_message_at)}</div>
          </div>
          <div className="text-xs text-stone-500 truncate">
            {(t.lead_id || t.ambulance_request_id || t.commerce_order_id) && <span className="font-bold text-stone-700">{t.ambulance_request_id ? tr("inbox.ctx.ambulance", "Ambulance") : t.commerce_order_id ? tr("inbox.ctx.order", "Order") : tr("inbox.ctx.service", "Service")} · </span>}
            {t.last_message || tr("inbox.new_conversation", "New conversation")}
          </div>
        </div>
      </Link>
      {t.unread > 0 ? <span className="bg-[#E05E36] text-white text-[10px] font-black px-2 py-0.5 rounded-full" data-testid={`inbox-thread-unread-${t.conv_id}`}>{t.unread}</span>
        : <button onClick={() => onMarkUnread(t)} title={tr("inbox.mark_unread", "Mark unread")} aria-label={tr("inbox.mark_unread", "Mark unread")} className="p-1.5 rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100"
                  data-testid={`inbox-thread-mark-unread-${t.conv_id}`}><Mail size={14} /></button>}
    </div>
  );
}

function AlertRow({ a, onOpen, onToggle }) {
  const { t } = useI18n();
  const Icon = CAT_ICON[a.category] || Bell;
  const test = a.deep_link?.metadata?.test_mode;
  return (
    <div className={`flex items-start gap-3 p-3 transition-colors ${a.read ? "bg-white" : "bg-amber-50/60"}`} data-testid={`inbox-alert-${a.notif_id}`} data-read={a.read ? "1" : "0"}>
      <button onClick={() => onOpen(a)} className="flex items-start gap-3 flex-1 min-w-0 text-left" data-testid={`inbox-alert-open-${a.notif_id}`}>
        <span className={`w-11 h-11 shrink-0 rounded-full grid place-items-center ${a.category === "emergency" ? "bg-red-100 text-red-700" : "bg-stone-100 text-stone-700"}`}><Icon size={18} /></span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center justify-between gap-2">
            <span className={`text-sm text-stone-900 truncate ${a.read ? "font-bold" : "font-black"}`}>{a.title}</span>
            <span className="text-[10px] text-stone-500 shrink-0">{relTime(a.created_at)}</span>
          </span>
          {(() => { const b = test ? (a.body || "").replace(/\s*·?\s*TEST MODE · no real money/, "").trim() : a.body; return b ? <span className="block text-xs text-stone-600 truncate">{b}</span> : null; })()}
          <span className="flex gap-1.5 mt-1">
            {test && <span className="rounded-full bg-amber-200 text-amber-900 px-2 text-[10px] font-black" data-testid={`inbox-alert-test-${a.notif_id}`}>{t("pay.test_mode", "TEST MODE · no real money")}</span>}
            <span className="text-[10px] text-stone-600">{t("inbox.in_app_only", "In-app only")}</span>
          </span>
        </span>
      </button>
      <button onClick={() => onToggle(a)} title={a.read ? t("inbox.mark_unread", "Mark unread") : t("inbox.mark_read", "Mark read")} aria-label={a.read ? t("inbox.mark_unread", "Mark unread") : t("inbox.mark_read", "Mark read")}
              className="p-1.5 rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100" data-testid={`inbox-alert-toggle-${a.notif_id}`}>
        {a.read ? <Mail size={14} /> : <MailOpen size={14} />}
      </button>
    </div>
  );
}

// Unified inbox · chat threads (/messenger/threads) + service/payment/trip alerts (/notifications?group=service).
export function UnifiedInbox() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const online = useOnline();
  const [tab, setTab] = useState("all");
  const [threads, setThreads] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [counts, setCounts] = useState({ messages_unread: 0, alerts_unread: 0, total: 0 });
  const [err, setErr] = useState({ t: false, a: false, c: false });

  const load = useCallback(async () => {
    if (document.hidden) return;
    const [t, a, c] = await Promise.allSettled([api.get("/messenger/threads"), api.get("/notifications", { params: { group: "service", limit: 50 } }),
      api.get("/inbox/unified-counts")]);
    if (t.status === "fulfilled") setThreads(t.value.data || []);
    if (a.status === "fulfilled") setAlerts(a.value.data?.items || []);
    if (c.status === "fulfilled") {
      setCounts(c.value.data);
      window.dispatchEvent(new CustomEvent("inbox:counts", { detail: c.value.data }));
    }
    setErr({ t: t.status === "rejected", a: a.status === "rejected", c: c.status === "rejected" });
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 10_000); return () => clearInterval(iv); }, [load]);

  const changed = () => window.dispatchEvent(new Event("inbox:changed"));
  const act = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast.success(okMsg); load(); changed(); }
    catch { if (navigator.onLine) toast.error(t("inbox.update_failed", "Couldn't update — retry in a moment")); }   // offline: the global banner already says so
  };
  const openAlert = async (a) => {
    if (!a.read) await api.patch(`/notifications/${a.notif_id}/read`).then(changed).catch(() => {});
    navigate(a.action_url || "/messenger");
  };
  const toggleAlert = (a) => act(() => api.patch(`/notifications/${a.notif_id}/${a.read ? "unread" : "read"}`));
  const markThreadUnread = (t) => act(() => api.post(`/messenger/thread/${t.conv_id}/unread`));

  const rows = useMemo(() => {
    const T = (threads || []).map((t) => ({ kind: "thread", at: t.last_message_at || "", t }));
    const A = (alerts || []).map((a) => ({ kind: "alert", at: a.created_at || "", a }));
    const list = tab === "messages" ? T : tab === "alerts" ? A : [...T, ...A];
    return list.sort((x, y) => (y.at > x.at ? 1 : -1));
  }, [threads, alerts, tab]);
  const error = err.t || err.a || err.c;
  const loading = threads === null && alerts === null && !error;
  const tabFailed = (tab === "alerts" && alerts === null && err.a) || (tab === "messages" && threads === null && err.t);

  const TABS = [["all", t("inbox.tab.all", "All"), counts.total], ["messages", t("inbox.tab.messages", "Messages"), counts.messages_unread], ["alerts", t("inbox.tab.alerts", "Alerts"), counts.alerts_unread]];
  return (
    <div data-testid="unified-inbox">
      <div className="flex items-center gap-2 mb-3 flex-wrap" role="tablist">
        {TABS.map(([k, label, n]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`inbox-tab-${k}`}
                  className={`h-9 px-4 rounded-full text-sm font-bold inline-flex items-center gap-1.5 transition-colors ${tab === k ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-stone-400"}`}>
            {label}{n > 0 && <span className={`text-[10px] font-black px-1.5 rounded-full ${tab === k ? "bg-white text-stone-900" : "bg-[#E05E36] text-white"}`} data-testid={`inbox-tab-count-${k}`}>{n}</span>}
          </button>
        ))}
        {tab === "alerts" && alerts !== null && counts.alerts_unread > 0 && (
          <button onClick={() => act(() => api.post("/notifications/mark-all-read", null, { params: { group: "service" } }), t("inbox.all_read_done", "All alerts marked read"))}
                  className="ms-auto text-xs font-bold text-sky-700 underline whitespace-nowrap" data-testid="inbox-mark-all-read">{t("inbox.mark_all_read", "Mark all read")}</button>
        )}
      </div>
      {!online && <div className="mb-3 rounded-xl bg-stone-900 text-white text-xs px-3 py-2 flex items-center gap-2" data-testid="inbox-offline"><WifiOff size={14} /> {t("inbox.offline", "You're offline — showing what was last loaded.")}</div>}
      {error && online && (threads !== null || alerts !== null) && (
        <div className="mb-3 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2 flex items-center gap-2" data-testid="inbox-partial-error">
          {t("inbox.partial_error", "Some items couldn't be refreshed.")} <button onClick={load} className="underline font-bold ms-auto">{t("common.retry", "Retry")}</button>
        </div>
      )}
      {loading ? <div className="text-center py-16 text-stone-500" data-testid="inbox-loading"><Loader2 className="inline animate-spin" /></div>
        : error && threads === null && alerts === null ? (
          <div className="text-center py-14 text-stone-600" data-testid="inbox-error">
            <p className="text-sm mb-3">{online ? t("inbox.load_failed", "Couldn't load your inbox.") : t("inbox.offline_short", "You're offline.")}</p>
            <button onClick={load} className="h-9 px-4 rounded-full border border-stone-300 font-bold text-sm inline-flex items-center gap-1.5"><RefreshCw size={14} /> {t("common.retry", "Retry")}</button>
          </div>
        ) : tabFailed ? (
          <div className="text-center py-14 text-stone-600" data-testid="inbox-tab-error">
            <p className="text-sm mb-3">{tab === "alerts" ? t("inbox.alerts_failed", "Couldn't load your alerts.") : t("inbox.threads_failed", "Couldn't load your conversations.")}</p>
            <button onClick={load} className="h-9 px-4 rounded-full border border-stone-300 font-bold text-sm inline-flex items-center gap-1.5"><RefreshCw size={14} /> {t("common.retry", "Retry")}</button>
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-stone-500" data-testid="inbox-empty">
            {tab === "alerts" ? <BellOff className="mx-auto mb-2 text-stone-300" size={32} /> : <MessageCircle className="mx-auto mb-2 text-stone-300" size={32} />}
            {tab === "alerts" ? t("inbox.empty_alerts", "No service, payment or trip alerts yet.") : tab === "messages" ? t("inbox.empty_messages", "No conversations yet. Start one above.") : t("inbox.empty_all", "Nothing here yet.")}
          </div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100 overflow-hidden" data-testid="inbox-list">
            {rows.map((r) => r.kind === "thread" ? <ThreadRow key={`t-${r.t.conv_id}`} t={r.t} onMarkUnread={markThreadUnread} />
              : <AlertRow key={`a-${r.a.notif_id}`} a={r.a} onOpen={openAlert} onToggle={toggleAlert} />)}
          </div>
        )}
    </div>
  );
}
